import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, Icon } from '@iwoca/orion';

import * as selectors from '../../../../api/repaymentScheduleSelector';
import { SHOW_REPAYMENT_SCHEDULE_MODAL } from '../../../modals/repaymentScheduleModal/RepaymentScheduleModal.actions';

import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals/MoneyWithDecimals';

import messages from './RepaymentScheduleSummary.messages';
import styles from './RepaymentScheduleSummary.module.css';
import LoadingScheduleValue from './LoadingScheduleValue';
import {
    getProductTypeFromURL,
    isInterestOnlyLoan,
    getLatestFormalOfferFromAccounts
} from '../../accountsSelector';
import { RootState } from '@iwProvider/configureStore';
import { Dispatch } from 'redux';
import { BlendedRateSummary } from './BlendedRateSummary/BlendedRateSummary';
import { getLoanDurationInMonths } from '../../../myOffers/MyOffers.helpers';

const mapStateToProps = (state: RootState) => {
    const productType = getProductTypeFromURL();
    const latestOffer = getLatestFormalOfferFromAccounts(state);

    return {
        durationInMonths: getLoanDurationInMonths(latestOffer?.max_duration),
        isInterestOnlyLoan: isInterestOnlyLoan(state, productType),
        finalMonthsRepaymentAmount: selectors.finalMonthsRepaymentAmount(state),
        equalMonthlyAmount: selectors.getDrawdownEqualRepaymentAmount(state),
        totalInterest: selectors.getDrawdownTotalInterest(state),
        totalBalance: selectors.getDrawdownTotalBalance(state),
        fees: selectors.getDrawdownFees(state),
        isLoading: state?.api?.draw_down_repayment_schedule?.pending,
        blendedRateDetails:
            state?.api?.draw_down_repayment_schedule?.data?.summary
                ?.blended_rate_details
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    showSchedule: () =>
        dispatch({
            type: SHOW_REPAYMENT_SCHEDULE_MODAL
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

function RepaymentScheduleSummary({
    showSchedule,
    durationInMonths,
    isInterestOnlyLoan,
    finalMonthsRepaymentAmount,
    equalMonthlyAmount,
    totalInterest,
    totalBalance,
    fees,
    isLoading,
    topupDrawdownAmount,
    blendedRateDetails
}: { topupDrawdownAmount?: number } & PropsFromRedux) {
    return (
        <div className={styles.repaymentScheduleContainer}>
            {topupDrawdownAmount !== undefined && (
                <BlendedRateSummary
                    topupDrawdownAmount={topupDrawdownAmount}
                    isLoading={isLoading}
                    blendedRateDetails={blendedRateDetails}
                />
            )}

            <div className={styles.repaymentScheduleTitleContainer}>
                <span className={styles.repaymentScheduleTitle}>
                    <FormattedMessage
                        {...messages.repaymentTitle}
                        values={{ monthsSchedule: durationInMonths }}
                    />
                </span>

                <Button
                    variant="tertiary"
                    onClick={showSchedule}
                    disabled={isLoading}
                >
                    <FormattedMessage {...messages.repaymentScheduleCTA} />
                    <Icon icon="calendarFilled" />
                </Button>
            </div>

            {isInterestOnlyLoan ? (
                <div className={styles.repaymentScheduleSummaryValueContainer}>
                    <div className={styles.interestOnlyMessage}>
                        <FormattedMessage
                            tagName="span"
                            {...messages.minimumRepaymentsLabelInterestOnly}
                        />
                        <FormattedMessage
                            tagName="span"
                            {...messages.minimumRepaymentsSubLabelInterestOnly}
                        />
                    </div>

                    <LoadingScheduleValue>
                        <MoneyWithDecimals
                            value={finalMonthsRepaymentAmount}
                            className={styles.repaymentScheduleSummaryValue}
                        />
                    </LoadingScheduleValue>
                </div>
            ) : (
                <div className={styles.repaymentScheduleSummaryValueContainer}>
                    <FormattedMessage {...messages.minimumRepaymentsLabel} />

                    <LoadingScheduleValue>
                        <MoneyWithDecimals
                            value={equalMonthlyAmount}
                            className={styles.repaymentScheduleSummaryValue}
                        />
                    </LoadingScheduleValue>
                </div>
            )}

            <div className={styles.repaymentScheduleSummaryValueContainer}>
                <FormattedMessage {...messages.totalInterestLabel} />

                <LoadingScheduleValue>
                    <MoneyWithDecimals
                        value={totalInterest}
                        className={styles.repaymentScheduleSummaryValue}
                    />
                </LoadingScheduleValue>
            </div>

            <div className={styles.repaymentScheduleSummaryValueContainer}>
                <FormattedMessage {...messages.feesLabel} />{' '}
                {durationInMonths === 24 ? (
                    <LoadingScheduleValue>
                        <MoneyWithDecimals
                            value={fees}
                            className={styles.repaymentScheduleSummaryValue}
                        />
                    </LoadingScheduleValue>
                ) : (
                    <MoneyWithDecimals
                        value={0}
                        className={styles.repaymentScheduleSummaryValue}
                    />
                )}
            </div>

            <div className={styles.repaymentScheduleSummaryValueContainer}>
                <FormattedMessage {...messages.totalRepaymentLabel} />

                <LoadingScheduleValue>
                    <MoneyWithDecimals
                        value={totalBalance}
                        className={styles.repaymentScheduleSummaryValue}
                    />
                </LoadingScheduleValue>
            </div>
        </div>
    );
}

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(RepaymentScheduleSummary);
